import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { FaGithub } from 'react-icons/fa';

import { getGithubAuthUrl } from '../../shared/api/integration';

// Unlike Jira/Trello, GitHub isn't synced in the background — it's used on demand by the coding
// agent (MVPB-67/68) to clone an existing repo, so there's no "Sync now" button here.
const GithubIntegration: React.FC<{
  isConnected?: boolean;
  onDisconnect: (integrationType: string) => void;
  onUpdate?: () => void;
}> = ({ isConnected, onDisconnect, onUpdate }) => {
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleGithubLogin = async () => {
    setErrorMessage(null);
    setIsConnecting(true);

    try {
      if (isConnected) {
        onDisconnect('github');
        return;
      }

      const { url } = await getGithubAuthUrl();
      if (url) {
        const popupWidth = 600;
        const popupHeight = 700;
        const left = window.innerWidth / 2 - popupWidth / 2;
        const top = window.innerHeight / 2 - popupHeight / 2;

        const popup = window.open(
          url,
          'github-auth-popup',
          `width=${popupWidth},height=${popupHeight},top=${top},left=${left}`,
        );

        if (popup) {
          const handleMessage = (e: MessageEvent) => {
            if (e.data?.type === 'github-connected') {
              window.removeEventListener('message', handleMessage);
              clearInterval(closeInterval);
              toast.success('GitHub connected successfully.');
              if (onUpdate) onUpdate();
            }
          };
          window.addEventListener('message', handleMessage);

          // Fallback: if popup closes without postMessage (user skipped), still refresh
          const closeInterval = setInterval(() => {
            if (popup.closed) {
              clearInterval(closeInterval);
              window.removeEventListener('message', handleMessage);
              if (onUpdate) onUpdate();
            }
          }, 1000);
        }
      } else {
        setErrorMessage('Failed to open GitHub authentication window.');
      }
    } catch (error) {
      console.error('Error during GitHub authentication:', error);
      setErrorMessage('An error occurred while connecting to GitHub.');
    } finally {
      setIsConnecting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <FaGithub className="text-2xl opacity-70" />
            <div className="text-lg font-semibold">GitHub</div>
            <span className={`badge ${isConnected ? 'badge-success' : 'badge-ghost'}`}>
              {isConnected ? 'Connected' : 'Not connected'}
            </span>
          </div>
          <div className="text-sm opacity-70">Connect GitHub so the coding agent can work on an existing repo.</div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={handleGithubLogin}
            disabled={isConnecting}
            className={`btn btn-sm ${isConnected ? 'btn-outline' : 'btn-primary'}`}
          >
            {isConnecting ? (
              <>
                <span className="loading loading-spinner loading-xs" />
                Connecting…
              </>
            ) : isConnected ? (
              'Disconnect'
            ) : (
              'Connect'
            )}
          </button>

          {isConnected && (
            <button className="btn btn-sm btn-ghost" onClick={onUpdate} disabled={isConnecting}>
              Refresh
            </button>
          )}
        </div>
      </div>

      {errorMessage && (
        <div className="alert alert-error">
          <span>{errorMessage}</span>
        </div>
      )}

      {!isConnected ? (
        <div className="alert bg-base-100 shadow-card">
          <span className="text-sm opacity-80">
            Click <b>Connect</b> to authorize GitHub access. You&apos;ll be able to point the coding agent at{' '}
            <code>owner/repo</code> once connected.
          </span>
        </div>
      ) : (
        <div className="alert alert-info">
          <span className="text-sm">
            Head to <b>Coding agent</b> to give it a task against one of your repos.
          </span>
        </div>
      )}

      <div className="collapse collapse-arrow bg-base-200">
        <input type="checkbox" />
        <div className="collapse-title font-medium">Permissions &amp; OAuth details</div>
        <div className="collapse-content text-sm opacity-80 space-y-2">
          <div className="font-medium">MFlow may request access to:</div>
          <ul className="list-disc list-inside space-y-1">
            <li>Read your repositories, so the coding agent can clone one to work on</li>
            <li>Nothing is pushed directly — the agent opens a pull request for review</li>
          </ul>

          <a
            className="link link-primary"
            rel="noreferrer"
            href="https://docs.github.com/en/apps/oauth-apps/building-oauth-apps"
            target="_blank"
          >
            How GitHub OAuth works →
          </a>
        </div>
      </div>
    </div>
  );
};

export default GithubIntegration;
